import { ApolloError, useMutation } from '@apollo/client';
import { ErrorBoundary, SuspenseLoader } from '@bechtel/client-common';
import { ADD_ROLE, GET_ROLES } from '@bechtel/client-gql';
import { Box, IconSize, Spinner, SpinnerSize, Text, theme } from '@bechtel/gds';
import { AddRoleError, AddRoleInput, AddRolePayload, AuthRole } from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { get } from 'lodash';
import React, { MutableRefObject, Suspense, useEffect, useState } from 'react';
import { RoleError } from '../error';
import { EditRole } from './edit';
import { ActionContainerStyled, ActionIconStyled, RoleItemContainerStyled } from './styled';
import { DownloadUsersList } from './user-download';

interface RoleItemProps {
  item: AuthRole;
  onDelete: (item: AuthRole) => void;
  isAddedRole: boolean;
  addedRoleIdRef: MutableRefObject<string>;
  getRoleNameForCopy: (name: string) => string;
}

export const RoleItem: React.FC<RoleItemProps> = ({
  item,
  onDelete,
  isAddedRole,
  addedRoleIdRef,
  getRoleNameForCopy,
}) => {
  const [showEditItem, setShowEditItem] = useState(isAddedRole || false);
  const { name, numUsers, sourceRole, id } = item;
  const disableAction = !sourceRole;
  const [addRole, { data, loading, error }] = useMutation<{ addRole: AddRolePayload }, { input: AddRoleInput }>(
    ADD_ROLE,
    {
      //@ts-ignore addRole will be there on data
      update: (cache, { data: { addRole } }) => {
        addedRoleIdRef.current = addRole.role.id;
        //@ts-ignore roles will be there from cache.readQuery
        const { roles } = cache.readQuery({ query: GET_ROLES });
        cache.writeQuery({
          query: GET_ROLES,
          data: { roles: roles.concat([addRole.role]) },
        });
      },
    }
  );
  const addRoleError = get(data, 'addRole.error') || error;
  const [roleError, setRoleError] = useState<AddRoleError | ApolloError | null>(null);

  useEffect(() => {
    if (!addRoleError) return;
    setRoleError(addRoleError);
  }, [addRoleError]);

  return (
    <>
      {!showEditItem ? (
        <RoleItemContainerStyled data-testid={`role-item-${item.sourceRole ? 'custom' : 'default'}-${item.id}`}>
          <Box flexGrow={{ sm: '1' }} justifyContent={{ sm: 'space-between' }} style={{ flex: '2 1' }}>
            <Box flexDirection={{ sm: 'column' }}>
              <Text family="graphik" size="300" color="grey.1000">
                {name}
              </Text>
              <Text family="graphik" size="200" color="black" style={{ marginTop: `${theme.spacings[400]}` }}>
                {sourceRole ? t`Custom Role` : t`Default Role`}
              </Text>
            </Box>
            <ActionContainerStyled>
              <ActionIconStyled
                size={IconSize.LARGE}
                icon="edit"
                fillColor="blue.800"
                disabled={disableAction}
                data-testid="edit-action"
                onClick={() => setShowEditItem(true)}
              ></ActionIconStyled>
              {loading ? (
                <Box ml={{ sm: '600' }}>
                  <Spinner size={SpinnerSize.MEDIUM}></Spinner>
                </Box>
              ) : (
                <ActionIconStyled
                  size={IconSize.LARGE}
                  icon="copy"
                  fillColor="blue.800"
                  disabled={false}
                  data-testid="copy-action"
                  onClick={async () => {
                    await addRole({
                      variables: {
                        input: {
                          name: getRoleNameForCopy(name),
                          sourceRoleId: id,
                        },
                      },
                    });
                  }}
                ></ActionIconStyled>
              )}
              <ActionIconStyled
                size={IconSize.LARGE}
                icon="trash-alt"
                fillColor="blue.800"
                disabled={disableAction}
                data-testid="delete-action"
                onClick={() => {
                  onDelete(item);
                }}
              ></ActionIconStyled>
            </ActionContainerStyled>
          </Box>
          <Box flexGrow={{ sm: '1' }} justifyContent={{ sm: 'flex-end' }} alignItems={{ sm: 'center' }}>
            <Box mr={{ sm: '700' }}>
              {!numUsers || numUsers == 0 ? (
                <Text color="blue.700" size="300">
                  <Trans>0 user</Trans>
                </Text>
              ) : (
                <Text color="blue.700" size="300">
                  {numUsers} {numUsers == 1 ? t`user` : t`users`}
                </Text>
              )}
            </Box>
            <DownloadUsersList numUsers={numUsers == undefined ? 0 : numUsers} roleId={item.id} roleName={item.name} />
          </Box>
        </RoleItemContainerStyled>
      ) : (
        <EditRole item={item} setShowEditItem={setShowEditItem}></EditRole>
      )}
      {roleError && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <RoleError roleError={roleError} onClose={() => setRoleError(null)}></RoleError>
          </Suspense>
        </ErrorBoundary>
      )}
    </>
  );
};
